export type TPinnableKind = "stated" | "computed" | "relation";

export type TPinnableFigure = {
  key: string;
  name?: string | null;
  group?: string | null;
  unit?: string | null;
  kind?: TPinnableKind | null;
};

export type TPinnableTwin = {
  headline?: { key?: unknown }[] | null;
  panels?: { headline?: { key?: unknown }[] | null }[] | null;
  vars?: Record<string, unknown> | null;
};

export type TPinnable = {
  key: string;
  name: string;
  group: string | null;
  unit: string | null;
  source: "figure" | "var";
};

export const MAX_PINNED_WIDGETS = 12;

export const asPinnableTwin = (twin: unknown): TPinnableTwin =>
  twin && typeof twin === "object" ? (twin as TPinnableTwin) : {};

const keysOf = (boxes: { key?: unknown }[] | null | undefined): string[] =>
  (Array.isArray(boxes) ? boxes : []).map((box) => (box && typeof box.key === "string" ? box.key : "")).filter(Boolean);

export const drawnFigures = (twin: TPinnableTwin): Set<string> => {
  const drawn = keysOf(twin.headline);
  for (const panel of Array.isArray(twin.panels) ? twin.panels : []) drawn.push(...keysOf(panel?.headline));
  return new Set(drawn);
};

export const pinnableFigures = (
  figures: TPinnableFigure[],
  twin: TPinnableTwin,
  alreadyPinned: Iterable<string> = []
): Map<string, TPinnable> => {
  const taken = new Set([...drawnFigures(twin), ...alreadyPinned]);

  const pinnable = new Map<string, TPinnable>();
  for (const figure of figures) {
    if (figure.kind === "relation" || taken.has(figure.key)) continue;
    pinnable.set(figure.key, {
      key: figure.key,
      name: figure.name || figure.key,
      group: figure.group ?? null,
      unit: figure.unit ?? null,
      source: "figure"
    });
  }
  for (const name of Object.keys(twin.vars ?? {})) {
    if (taken.has(name) || pinnable.has(name)) continue;
    pinnable.set(name, { key: name, name, group: null, unit: null, source: "var" });
  }
  return pinnable;
};
